import { ASSESSMENT_BENCHMARK_SLOT_PLAN } from '../constants';
import { filterAssessmentCandidatesByExposure, selectAssessmentCandidate } from '../selection';
import type {
  AssessmentRendererType,
  AssessmentSkillType,
  IAssessmentCandidate,
  IAssessmentExposure,
} from '../types';

export interface IAssessmentSupplyIssue {
  available: number;
  required: number;
  renderer: AssessmentRendererType;
  score: number;
  skill: AssessmentSkillType;
  plan: string;
}

const SIMULATED_ATTEMPT_COUNT = 4;
const SUPPLY_DIFFICULTY_WINDOW = 15;

export function simulateAssessmentCandidateSupply(
  candidates: readonly IAssessmentCandidate[],
  score: number,
  slotPlan: readonly Readonly<
    import('../types').IAssessmentSlot
  >[] = ASSESSMENT_BENCHMARK_SLOT_PLAN,
  plan = 'benchmark',
): IAssessmentSupplyIssue[] {
  const bandCandidates = candidates.filter(
    (candidate) => Math.abs(candidate.difficulty - score) <= SUPPLY_DIFFICULTY_WINDOW,
  );
  const exposures: IAssessmentExposure[] = [];
  const issues: IAssessmentSupplyIssue[] = [];
  for (let attemptIndex = 0; attemptIndex < SIMULATED_ATTEMPT_COUNT; attemptIndex += 1) {
    const currentAttemptItemIds = new Set<string>();
    for (const [step, slot] of slotPlan.entries()) {
      const requiredCandidateCount = slotPlan
        .slice(step)
        .filter(
          (plannedSlot) =>
            plannedSlot.renderer === slot.renderer && plannedSlot.skill === slot.skill,
        ).length;
      const eligible = filterAssessmentCandidatesByExposure({
        candidates: bandCandidates,
        currentAttemptItemIds,
        exposures,
        now: attemptIndex,
        plannedItemCount: slotPlan.length,
        requiredCandidateCount,
      });
      const selected = selectAssessmentCandidate({
        attemptId: `supply:${score}:${attemptIndex}`,
        candidates: eligible,
        excludedItemIds: currentAttemptItemIds,
        slot,
        step,
        targetDifficulty: score,
      });
      if (!selected) {
        issues.push({
          available: bandCandidates.filter(
            (candidate) => candidate.renderer === slot.renderer && candidate.skill === slot.skill,
          ).length,
          renderer: slot.renderer,
          plan,
          required: requiredCandidateCount,
          score,
          skill: slot.skill,
        });
        break;
      }
      currentAttemptItemIds.add(selected.id);
    }
    exposures.push(
      ...[...currentAttemptItemIds].map((itemId) => ({ itemId, lastSeenAt: attemptIndex })),
    );
  }
  return issues;
}
